import React, { useState, useRef, useEffect } from 'react';
import { useSpc } from '../../context/SpcContext';
import { Flag, Trophy, RotateCcw, Loader2, Medal, ChevronLeft, ChevronRight } from 'lucide-react';

// Logical canvas coordinate system (scaled by CSS to fit container)
const W = 360, H = 540;
const RUNNER_Y = H - 96;
const RUNNER_SZE = 46;
const MARGIN = 34;

const BADGE_META = {
  perfect_pacing: { label: 'Clean Run', icon: '🎯' },
  fuelled_properly: { label: 'Fuelled Properly', icon: '⚡' },
  negative_split: { label: 'Distance Demon', icon: '📈' },
  bonk_survivor: { label: 'Bonk Survivor', icon: '💥' },
  power_collector: { label: 'Power Player', icon: '🧲' },
};

const TYPES = {
  cone:  { emoji: '🚧', kind: 'obstacle', size: 34 },
  dog:   { emoji: '🐕', kind: 'obstacle', size: 36 },
  rock:  { emoji: '🪨', kind: 'obstacle', size: 34 },
  puddle:{ emoji: '💦', kind: 'obstacle', size: 34 },
  water: { emoji: '💧', kind: 'water', size: 32 },
  gel:   { emoji: '🧃', kind: 'gel', size: 32 },
  boost: { emoji: '⭐', kind: 'boost', size: 34 },
  magnet:{ emoji: '🧲', kind: 'magnet', size: 34 },
  shield:{ emoji: '🛡️', kind: 'shield', size: 34 },
};

const MAGNET_MS = 5000;
const MAX_SHIELDS = 2;

function freshGame() {
  return {
    playerX: W / 2, targetX: W / 2, moveDir: 0, dragging: false,
    entities: [], nextSpawn: 500,
    distance: 0, score: 0, energy: 100, minEnergy: 100,
    fuels: 0, boosts: 0, hits: 0, magnets: 0, shields: 0, shieldsCollected: 0,
    invincibleUntil: 0, hurtUntil: 0, speedBoostUntil: 0, magnetUntil: 0,
    laneOffset: 0, last: 0, startTs: 0, elapsed: 0, lastRender: 0, ended: false,
  };
}

export default function RaceDay() {
  const ctx = useSpc();
  const canvasRef = useRef(null);
  const gs = useRef(freshGame());
  const rafRef = useRef(null);
  const phaseRef = useRef('idle');

  const [phase, setPhase] = useState('idle');
  const [hud, setHud] = useState({ distance: 0, score: 0, energy: 100, shields: 0, magnetSecs: 0 });
  const [end, setEnd] = useState(null);
  const [board, setBoard] = useState(null);
  const [loadingBoard, setLoadingBoard] = useState(true);

  const loadBoard = async () => {
    const d = await ctx.getRaceDayLeaderboard();
    setBoard(d); setLoadingBoard(false);
  };

  // Fetch leaderboard once on mount (NOT tied to RAF lifecycle)
  useEffect(() => { let alive = true; (async () => { const d = await ctx.getRaceDayLeaderboard(); if (alive) { setBoard(d); setLoadingBoard(false); } })(); return () => { alive = false; }; }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Cancel RAF only on unmount
  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);
  // Draw an idle frame on mount
  useEffect(() => { draw(gs.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- drawing ----------
  function draw(g) {
    const cv = canvasRef.current; if (!cv) return;
    const c = cv.getContext('2d');
    c.clearRect(0, 0, W, H);
    // track
    c.fillStyle = '#0d0d0f'; c.fillRect(0, 0, W, H);
    c.fillStyle = 'rgba(255,255,255,0.03)'; c.fillRect(MARGIN - 14, 0, W - (MARGIN - 14) * 2, H);
    // moving lane dashes
    c.strokeStyle = 'rgba(225,226,10,0.18)'; c.lineWidth = 4; c.setLineDash([26, 26]);
    c.lineDashOffset = -g.laneOffset;
    for (const lx of [W / 2]) { c.beginPath(); c.moveTo(lx, 0); c.lineTo(lx, H); c.stroke(); }
    c.setLineDash([]);
    // edges
    c.strokeStyle = 'rgba(255,255,255,0.10)'; c.lineWidth = 3;
    c.beginPath(); c.moveTo(MARGIN - 14, 0); c.lineTo(MARGIN - 14, H); c.moveTo(W - MARGIN + 14, 0); c.lineTo(W - MARGIN + 14, H); c.stroke();
    // entities
    c.textAlign = 'center'; c.textBaseline = 'middle';
    for (const e of g.entities) {
      const t = TYPES[e.type];
      const r = t.size / 2 + 6;
      const col = t.kind === 'obstacle' ? '#e5484d'
        : t.kind === 'water' ? '#3aa0ff'
        : t.kind === 'gel' ? '#8be04f'
        : t.kind === 'magnet' ? '#22d3ee'
        : t.kind === 'shield' ? '#ff9f43'
        : '#ffd60a';
      c.beginPath(); c.arc(e.x, e.y, r, 0, Math.PI * 2);
      c.fillStyle = col + '33'; c.fill();
      c.lineWidth = 2.5; c.strokeStyle = col; c.stroke();
      if (t.kind === 'boost' || t.kind === 'magnet' || t.kind === 'shield') { c.shadowColor = col; c.shadowBlur = 18; c.stroke(); c.shadowBlur = 0; }
      c.font = `${t.size}px serif`;
      c.fillStyle = '#fff';
      c.fillText(t.emoji, e.x, e.y + 1);
    }
    // runner
    const now = performance.now();
    const inv = now < g.invincibleUntil;
    const hurt = now < g.hurtUntil;
    // magnet aura
    if (now < g.magnetUntil) {
      const pulse = 1 + 0.12 * Math.sin(now / 90);
      c.beginPath(); c.arc(g.playerX, RUNNER_Y, 58 * pulse, 0, Math.PI * 2);
      c.strokeStyle = 'rgba(34,211,238,0.4)'; c.lineWidth = 3; c.stroke();
    }
    // shield rings
    for (let i = 0; i < g.shields; i++) {
      c.beginPath(); c.arc(g.playerX, RUNNER_Y, RUNNER_SZE / 2 + 9 + i * 7, 0, Math.PI * 2);
      c.strokeStyle = 'rgba(255,159,67,0.85)'; c.lineWidth = 2.5; c.setLineDash([5, 4]); c.stroke(); c.setLineDash([]);
    }
    c.globalAlpha = hurt ? (Math.floor(now / 80) % 2 ? 0.4 : 1) : 1;
    c.beginPath(); c.arc(g.playerX, RUNNER_Y, RUNNER_SZE / 2 - 2, 0, Math.PI * 2);
    c.fillStyle = inv ? 'rgba(225,226,10,0.9)' : 'rgba(225,226,10,0.85)';
    c.shadowColor = '#e1e20a'; c.shadowBlur = inv ? 28 : 14; c.fill(); c.shadowBlur = 0;
    c.font = `${RUNNER_SZE - 6}px serif`;
    c.fillStyle = '#111';
    c.fillText('🏃', g.playerX, RUNNER_Y + 1);
    c.globalAlpha = 1;
  }

  // ---------- loop ----------
  function endGame(g) {
    if (g.ended) return;
    g.ended = true;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const distance = Math.round(g.distance);
    const score = Math.max(0, Math.round(g.distance + g.fuels * 20 + g.boosts * 15 + g.magnets * 15 + g.shieldsCollected * 15 + (g.hits === 0 ? 150 : 0)));
    const badges = [];
    if (g.hits === 0 && distance >= 700) badges.push('perfect_pacing');
    if (g.fuels >= 8) badges.push('fuelled_properly');
    if (distance >= 1800) badges.push('negative_split');
    if (g.minEnergy <= 15 && distance >= 700) badges.push('bonk_survivor');
    if (g.magnets >= 1 && g.shieldsCollected >= 1) badges.push('power_collector');

    setPhase('done'); phaseRef.current = 'done';
    setHud({ distance, score, energy: 0, shields: 0, magnetSecs: 0 });
    setEnd({ score, badges, distance, fuels: g.fuels, magnets: g.magnets, shieldsCollected: g.shieldsCollected, submitting: true, submit: null });
    (async () => {
      const res = await ctx.raceDayComplete({ score, badges, timeMs: Math.round(g.elapsed), distance });
      setEnd(prev => ({ ...prev, submitting: false, submit: res }));
      loadBoard();
    })();
  }

  function collide(g, e) {
    const half = TYPES[e.type].size / 2 + 4;
    const rHalf = RUNNER_SZE / 2 - 6;
    return Math.abs(e.x - g.playerX) < (half + rHalf) && Math.abs(e.y - RUNNER_Y) < (half + rHalf);
  }

  function spawn(g) {
    // Obstacle mix skews harder the further you get — fewer safe pickups, more obstacles.
    const obstacleBias = Math.min(0.25, g.distance * 0.00004);
    const roll = Math.random();
    let type;
    if (roll < 0.40 + obstacleBias) type = ['cone', 'dog', 'rock', 'puddle'][Math.floor(Math.random() * 4)];
    else if (roll < 0.58 + obstacleBias * 0.6) type = 'water';
    else if (roll < 0.76 + obstacleBias * 0.3) type = 'gel';
    else if (roll < 0.85) type = 'boost';
    else if (roll < 0.925) type = 'magnet';
    else if (roll < 1.0) type = 'shield';
    else type = ['cone', 'dog'][Math.floor(Math.random() * 2)];
    const x = MARGIN + Math.random() * (W - MARGIN * 2);
    g.entities.push({ type, x, y: -30 });
    // Past 2.5km, obstacles start arriving in extra side-by-side bursts — the track genuinely gets busier.
    const extraObstacles = Math.min(2, Math.floor(g.distance / 2500));
    for (let i = 0; i < extraObstacles; i++) {
      const ox = MARGIN + Math.random() * (W - MARGIN * 2);
      g.entities.push({ type: ['cone', 'dog', 'rock', 'puddle'][Math.floor(Math.random() * 4)], x: ox, y: -30 - (i + 1) * 60 });
    }
  }

  function loop(ts) {
    const g = gs.current;
    if (phaseRef.current !== 'playing' || g.ended) return;
    if (!g.last) g.last = ts;
    let dt = (ts - g.last) / 1000; g.last = ts;
    if (dt > 0.05) dt = 0.05;
    g.elapsed = ts - g.startTs;
    const now = ts;

    // speed ramps with distance — a fast early ramp plus a slow-but-endless linear climb, so
    // there's no plateau to hide at forever: the run keeps getting harder the longer it goes.
    const boosting = now < g.speedBoostUntil;
    const speedRamp = 230 * (1 - Math.exp(-g.distance / 2200)) + g.distance * 0.02;
    const baseSpeed = 175 + speedRamp;
    const speed = baseSpeed * (boosting ? 1.65 : 1);
    g.distance += speed * dt * 0.16;
    g.laneOffset = (g.laneOffset + speed * dt) % 52;

    // passive energy drain (slower while boosting) — also creeps up with distance
    g.energy -= (boosting ? 1.5 : 4 + Math.min(2, g.distance * 0.0006)) * dt;

    // player movement
    const moveSpeed = 340;
    if (g.dragging) {
      const d = g.targetX - g.playerX;
      g.playerX += Math.max(-moveSpeed * dt, Math.min(moveSpeed * dt, d));
    } else if (g.moveDir) {
      g.playerX += g.moveDir * moveSpeed * dt;
    }
    g.playerX = Math.max(MARGIN, Math.min(W - MARGIN, g.playerX));

    // spawn
    g.nextSpawn -= dt * 1000;
    if (g.nextSpawn <= 0) {
      spawn(g);
      const gap = Math.max(280, 780 - g.distance * 0.15);
      g.nextSpawn = gap + Math.random() * 260;
    }

    // move entities + collisions
    const magnetOn = now < g.magnetUntil;
    const keep = [];
    for (const e of g.entities) {
      e.y += speed * dt;
      const kind = TYPES[e.type].kind;
      if (magnetOn && kind !== 'obstacle' && e.y > 0 && e.y < RUNNER_Y + 30) {
        const pull = 300 * dt;
        const d = g.playerX - e.x;
        e.x += Math.max(-pull, Math.min(pull, d));
      }
      if (e.y > H + 40) continue;
      if (collide(g, e)) {
        if (kind === 'obstacle') {
          if (now >= g.invincibleUntil) {
            if (g.shields > 0) { g.shields -= 1; g.hurtUntil = now + 400; continue; }
            g.energy -= 25; g.hits += 1; g.hurtUntil = now + 500;
            continue; // remove
          } else { keep.push(e); continue; }
        } else if (kind === 'water') { g.energy = Math.min(100, g.energy + 12); g.fuels += 1; g.score += 20; continue; }
        else if (kind === 'gel') { g.energy = Math.min(100, g.energy + 18); g.fuels += 1; g.score += 30; continue; }
        else if (kind === 'boost') { g.speedBoostUntil = now + 3800; g.invincibleUntil = now + 3800; g.boosts += 1; g.score += 15; continue; }
        else if (kind === 'magnet') { g.magnetUntil = now + MAGNET_MS; g.magnets += 1; g.score += 15; continue; }
        else if (kind === 'shield') { g.shields = Math.min(MAX_SHIELDS, g.shields + 1); g.shieldsCollected += 1; g.score += 15; continue; }
      }
      keep.push(e);
    }
    g.entities = keep;

    if (g.energy < g.minEnergy) g.minEnergy = g.energy;
    if (g.energy <= 0) { g.energy = 0; draw(g); endGame(g); return; }

    draw(g);
    if (ts - g.lastRender > 90) {
      g.lastRender = ts;
      setHud({
        distance: Math.round(g.distance),
        score: Math.round(g.distance + g.fuels * 20 + g.boosts * 15 + g.magnets * 15 + g.shieldsCollected * 15),
        energy: g.energy,
        shields: g.shields,
        magnetSecs: magnetOn ? Math.max(0, Math.ceil((g.magnetUntil - ts) / 1000)) : 0,
      });
    }
    rafRef.current = requestAnimationFrame(loop);
  }

  function start() {
    gs.current = freshGame();
    gs.current.startTs = performance.now();
    setEnd(null);
    setHud({ distance: 0, score: 0, energy: 100, shields: 0, magnetSecs: 0 });
    setPhase('playing'); phaseRef.current = 'playing';
    rafRef.current = requestAnimationFrame(loop);
  }

  // ---------- input ----------
  const canvasX = (clientX) => {
    const cv = canvasRef.current; const r = cv.getBoundingClientRect();
    return ((clientX - r.left) / r.width) * W;
  };
  const onDown = (e) => { if (phaseRef.current !== 'playing') return; gs.current.dragging = true; gs.current.targetX = canvasX(e.clientX); };
  const onMove = (e) => { if (phaseRef.current !== 'playing' || !gs.current.dragging) return; gs.current.targetX = canvasX(e.clientX); };
  const onUp = () => { gs.current.dragging = false; };
  const setDir = (d) => { gs.current.moveDir = d; };

  useEffect(() => {
    const kd = (e) => { if (e.key === 'ArrowLeft') setDir(-1); if (e.key === 'ArrowRight') setDir(1); };
    const ku = (e) => { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') setDir(0); };
    window.addEventListener('keydown', kd); window.addEventListener('keyup', ku);
    return () => { window.removeEventListener('keydown', kd); window.removeEventListener('keyup', ku); };
  }, []);

  const energyLow = hud.energy < 25;

  return (
    <div className="solo-game raceday" data-testid="solo-raceday">
      <div className="solo-head">
        <div className="solo-title"><Flag size={18} /><span>SPC Race Day</span></div>
        <div className="solo-score">
          {board?.myBest > 0 && <span className="pill yellow"><Trophy size={12} /> PB {board.myBest}</span>}
          {phase === 'playing' && <span className="pill">Score {hud.score}</span>}
        </div>
      </div>

      {phase === 'idle' && (
        <div className="raceday-intro" data-testid="raceday-intro">
          <p>An arcade dash! <strong>Slide left &amp; right</strong> to dodge cones, dogs and puddles while grabbing <strong>water 💧 &amp; gels 🧃</strong> to keep your energy up. Grab a <strong>⭐ boost</strong> for a burst of speed and invincibility, a <strong>🧲 magnet</strong> to pull nearby pickups toward you, or a <strong>🛡️ shield</strong> to absorb a hit for free. The further you run, the faster and busier it gets — with more obstacles piling in the longer you last — so how far can you go?</p>
          <button className="btn raceday-start" onClick={start} data-testid="raceday-start"><Flag size={16} /> Start the race</button>
          {board?.playedToday && <p className="steprace-hint">You've already run today — race again to smash your PB &amp; badges!</p>}
        </div>
      )}

      {(phase === 'playing' || phase === 'done') && (
        <div className="raceday-play" data-testid="raceday-play">
          <div className="raceday-hud">
            <span data-testid="raceday-distance">🏁 {hud.distance} m</span>
            <span>⭐ {hud.score}</span>
          </div>
          {(hud.shields > 0 || hud.magnetSecs > 0) && (
            <div className="raceday-powers" data-testid="raceday-powers">
              {hud.shields > 0 && <span className="pill orange" data-testid="raceday-shield-pill">🛡️ x{hud.shields}</span>}
              {hud.magnetSecs > 0 && <span className="pill cyan" data-testid="raceday-magnet-pill">🧲 {hud.magnetSecs}s</span>}
            </div>
          )}
          <div className={`raceday-energy ${energyLow ? 'low' : ''}`} data-testid="raceday-energy">
            <div className="raceday-energy-label"><span>Energy</span><span>{Math.max(0, Math.round(hud.energy))}%</span></div>
            <div className="raceday-energy-bar"><div className="raceday-energy-fill" style={{ width: `${Math.max(0, hud.energy)}%` }} /></div>
          </div>

          <div className="raceday-canvas-wrap">
            <canvas
              ref={canvasRef} width={W} height={H} className="raceday-canvas" data-testid="raceday-canvas"
              onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp} onPointerCancel={onUp}
            />
          </div>

          {phase === 'playing' && (
            <div className="raceday-dpad">
              <button className="btn raceday-arrow" onPointerDown={(e) => { e.preventDefault(); setDir(-1); }} onPointerUp={() => setDir(0)} onPointerLeave={() => setDir(0)} data-testid="raceday-left"><ChevronLeft size={26} /></button>
              <button className="btn raceday-arrow" onPointerDown={(e) => { e.preventDefault(); setDir(1); }} onPointerUp={() => setDir(0)} onPointerLeave={() => setDir(0)} data-testid="raceday-right"><ChevronRight size={26} /></button>
            </div>
          )}
        </div>
      )}

      {phase === 'done' && end && (
        <div className="raceday-result" data-testid="raceday-result">
          <div className="raceday-score-big">{end.score}<span>pts</span></div>
          <p className="raceday-outcome">You ran <strong>{end.distance} m</strong> · {end.fuels} fuel{end.fuels === 1 ? '' : 's'}{end.magnets > 0 ? ` · ${end.magnets} magnet${end.magnets === 1 ? '' : 's'}` : ''}{end.shieldsCollected > 0 ? ` · ${end.shieldsCollected} shield${end.shieldsCollected === 1 ? '' : 's'}` : ''}</p>

          {end.badges.length > 0 && (
            <div className="raceday-badges" data-testid="raceday-badges">
              {end.badges.map(b => <span key={b} className="raceday-badge">{BADGE_META[b]?.icon} {BADGE_META[b]?.label}</span>)}
            </div>
          )}

          {end.submitting ? (
            <div className="raceday-award"><Loader2 size={16} className="spin" /> Saving your run…</div>
          ) : end.submit ? (
            <div className="raceday-award" data-testid="raceday-award">
              {end.submit.newPB
                ? <span className="pill yellow">New Personal Best! 🎉</span>
                : <span className="pill">PB {end.submit.best} — beat it next run!</span>}
            </div>
          ) : null}

          <button className="btn raceday-start" onClick={start} data-testid="raceday-again"><RotateCcw size={15} /> Race again</button>
        </div>
      )}

      <div className="raceday-lb" data-testid="raceday-leaderboard">
        <div className="raceday-lb-head"><Medal size={15} /> This week's top runners</div>
        {loadingBoard ? (
          <div className="empty" style={{ padding: 14 }}><Loader2 size={16} className="spin" /></div>
        ) : (board?.leaderboard?.length ? (
          <div className="raceday-lb-list">
            {board.leaderboard.map((r, i) => (
              <div key={r.userId} className={`raceday-lb-row ${r.isMe ? 'me' : ''}`} data-testid={`raceday-lb-row-${i}`}>
                <span className="raceday-lb-rank">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}</span>
                <span className="raceday-lb-name">{r.name}{r.isMe ? ' (you)' : ''}</span>
                <span className="raceday-lb-score">{r.score}</span>
              </div>
            ))}
          </div>
        ) : <div className="empty" style={{ padding: 14 }}>No runs yet this week — be the first!</div>)}
      </div>
    </div>
  );
}
